// src/components/BannerSlider.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { B, fonts } from "./UI";
import { useBranding } from "../hooks/useBranding";
import { withCloudinaryBannerOptimization, withCloudinaryAutoFormatQuality } from "../utils/cloudinary";

const VIDEO_EXT_RE = /\.(mp4|webm|ogg|mov)(\?.*)?$/i;
const PRELOAD_AHEAD = 2; // preload next N slides

const getYouTubeEmbedUrl = (url = "") => {
  if (!url) return null;
  let id = null;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      id = parsed.pathname.split("/").filter(Boolean)[0] || null;
    } else if (host === "youtube.com" || host === "m.youtube.com") {
      if (parsed.pathname === "/watch") id = parsed.searchParams.get("v");
      else if (parsed.pathname.startsWith("/embed/") || parsed.pathname.startsWith("/shorts/") || parsed.pathname.startsWith("/live/")) {
        id = parsed.pathname.split("/")[2] || null;
      }
    }
  } catch {
    return null;
  }
  if (!id) return null;
  return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&rel=0&modestbranding=1&controls=1&enablejsapi=1&playsinline=1`;
};

export default function BannerSlider({ banners }) {
  const { branding }              = useBranding();
  const [current, setCurrent]     = useState(0);
  const [paused,  setPaused]      = useState(false);
  const [touched, setTouched]     = useState(null);
  const youtubeFrameRef           = useRef(null);
  const preloadedRef              = useRef(new Set());

  const active = banners.filter(b => b.active);
  const next   = useCallback(() => setCurrent(c => (c + 1) % active.length), [active.length]);
  const prev   = ()  => setCurrent(c => (c - 1 + active.length) % active.length);

  const slide  = active[current] || active[0];
  const accent = branding.primaryColor || B.orange;
  const videoUrl = (slide?.videoUrl || "").trim();
  const imageUrl = withCloudinaryBannerOptimization((slide?.imageUrl || "").trim());
  const youtubeEmbedUrl = getYouTubeEmbedUrl(videoUrl);
  const isYouTubeVideo = Boolean(youtubeEmbedUrl);
  const isDirectVideo = VIDEO_EXT_RE.test(videoUrl);

  // Preload upcoming slides' images so they're cached before being displayed
  useEffect(() => {
    if (active.length < 2) return;
    for (let i = 1; i <= PRELOAD_AHEAD; i++) {
      const idx = (current + i) % active.length;
      const slideToPreload = active[idx];
      if (!slideToPreload) continue;
      const rawUrl = (slideToPreload.imageUrl || "").trim();
      if (!rawUrl) continue;
      const optimizedUrl = withCloudinaryBannerOptimization(rawUrl);
      // Only preload images, not YouTube/direct videos — they handle themselves
      if (VIDEO_EXT_RE.test(slideToPreload.videoUrl || "") || getYouTubeEmbedUrl(slideToPreload.videoUrl)) continue;
      if (preloadedRef.current.has(optimizedUrl)) continue;
      preloadedRef.current.add(optimizedUrl);
      const img = new Image();
      img.src = optimizedUrl;
    }
  }, [current, active]);

  // Auto-advance timer for image slides
  useEffect(() => {
    if (paused || active.length < 2 || isDirectVideo || isYouTubeVideo) return;
    const t = setInterval(next, 4800);
    return () => clearInterval(t);
  }, [next, paused, active.length, isDirectVideo, isYouTubeVideo]);

  // YouTube API integration
  useEffect(() => {
    if (!isYouTubeVideo || paused || active.length < 2 || !youtubeFrameRef.current) return;
    let player = null;
    let cancelled = false;

    const mountPlayer = () => {
      if (cancelled || !youtubeFrameRef.current || !window.YT?.Player) return;
      player = new window.YT.Player(youtubeFrameRef.current, {
        events: {
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.ENDED) next();
          },
        },
      });
    };

    if (window.YT?.Player) {
      mountPlayer();
    } else {
      const existing = document.getElementById("youtube-iframe-api");
      if (!existing) {
        const script = document.createElement("script");
        script.id = "youtube-iframe-api";
        script.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(script);
      }
      const prevReady = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        prevReady?.();
        mountPlayer();
      };
    }

    return () => {
      cancelled = true;
      player?.destroy?.();
    };
  }, [isYouTubeVideo, paused, active.length, next, current]);

  // Touch swipe support
  const onTouchStart = e => setTouched(e.touches[0].clientX);
  const onTouchEnd   = e => {
    if (touched === null) return;
    const diff = touched - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) diff > 0 ? next() : prev();
    setTouched(null);
  };

  if (!active.length) return null;

  // Render all active slides simultaneously to avoid re-mounting <img> elements
  // Only the current slide is visible; others are hidden via CSS opacity/pointer-events
  // This keeps image elements alive and cached in the browser, preventing re-downloads on each slide transition.
  const renderSlide = (banner, index) => {
    const isActive = index === current;
    const bannerVideoUrl = (banner?.videoUrl || "").trim();
    const bannerImageUrl = withCloudinaryBannerOptimization((banner?.imageUrl || "").trim());
    const bannerYoutubeEmbedUrl = getYouTubeEmbedUrl(bannerVideoUrl);
    const bannerIsYouTubeVideo = Boolean(bannerYoutubeEmbedUrl);
    const bannerIsDirectVideo = VIDEO_EXT_RE.test(bannerVideoUrl);

    return (
      <div
        key={banner.id || index}
        style={{
          position: "absolute",
          inset: 0,
          opacity: isActive ? 1 : 0,
          pointerEvents: isActive ? "auto" : "none",
          zIndex: isActive ? 1 : 0,
          transition: "opacity 0.5s ease",
        }}
        aria-hidden={!isActive}
      >
        {bannerIsYouTubeVideo
            ? (
              <iframe
                ref={isActive ? youtubeFrameRef : undefined}
                src={bannerYoutubeEmbedUrl}
                title={banner.title || "Banner video"}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0, animation: isActive ? "fadeIn 0.5s" : "none" }}
              />
            )
            : bannerIsDirectVideo
            ? (
              <video
                src={bannerVideoUrl}
                autoPlay
                muted
                playsInline
                preload="metadata"
                aria-label={banner.title || "Banner video"}
                onEnded={() => active.length > 1 && next()}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />
            )
            : bannerImageUrl
            ? <img src={bannerImageUrl} alt={banner.title || "Banner"}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
            : <div style={{ position: "absolute", inset: 0, background: `linear-gradient(135deg, ${B.orangeFaint}, ${B.orangeMid})` }} />
        }
        {/* Overlay */}
        <div style={{ position: "absolute", inset: 0}} />

        {/* Text */}
        <div style={{ position: "absolute", top: "50%", left: "5%", transform: "translateY(-50%)", maxWidth: "48%" }}>
          {banner.badge && (
            <span style={{ display: "inline-block", background: accent, color: B.text, fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", padding: "4px 12px", borderRadius: 4, marginBottom: 12, fontFamily: fonts.body }}>
              {banner.badge}
            </span>
          )}
          {banner.title && (
            <h2 style={{ margin: "0 0 10px", fontSize: "clamp(18px,3.2vw,36px)", fontWeight: 700, color: B.text, fontFamily: fonts.display, lineHeight: 1.2, textShadow: "0 8px 20px rgba(255,255,255,0.75)" }}>
              {banner.title}
            </h2>
          )}
          {banner.subtitle && (
            <p style={{ margin: "0 0 14px", fontSize: "clamp(11px,1.4vw,15px)", color: B.textMid, lineHeight: 1.6, fontFamily: fonts.body }}>
              {banner.subtitle}
            </p>
          )}
          {banner.price && (
            <div style={{ fontSize: "clamp(16px,2.5vw,28px)", fontWeight: 700, color: accent, fontFamily: fonts.display }}>
              {banner.price}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{ position: "relative", width: "100%", borderRadius: 24, overflow: "hidden", boxShadow: "0 20px 48px rgba(224,107,79,0.2)", marginBottom: 0, background: "#fff5ee", userSelect: "none", border: `1px solid ${B.borderLight}` }}
    >
      {/* Slides container — all slides rendered */}
      <div style={{ position: "relative", paddingBottom: "36%", minHeight: 200 }}>
        {active.map((banner, index) => renderSlide(banner, index))}
      </div>

      {/* Arrows */}
      {active.length > 1 && (
        <>
          <button onClick={prev} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.82)", backdropFilter: "blur(4px)", border: `1px solid ${B.border}`, color: B.text, width: 38, height: 38, borderRadius: "50%", cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s", zIndex: 2 }}
            onMouseEnter={e => e.target.style.background="rgba(255,255,255,1)"}
            onMouseLeave={e => e.target.style.background="rgba(255,255,255,0.82)"}>‹</button>
          <button onClick={next} style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.82)", backdropFilter: "blur(4px)", border: `1px solid ${B.border}`, color: B.text, width: 38, height: 38, borderRadius: "50%", cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s", zIndex: 2 }}
            onMouseEnter={e => e.target.style.background="rgba(255,255,255,1)"}
            onMouseLeave={e => e.target.style.background="rgba(255,255,255,0.82)"}>›</button>
        </>
      )}

      {/* Dots */}
      {active.length > 1 && (
        <div style={{ position: "absolute", bottom: 14, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 7, zIndex: 2 }}>
          {active.map((_, i) => (
            <div key={i} onClick={() => setCurrent(i)}
              style={{ width: i === current ? 24 : 8, height: 8, borderRadius: 4, background: i === current ? accent : "rgba(122,98,86,0.3)", cursor: "pointer", transition: "all 0.35s" }} />
          ))}
        </div>
      )}
    </div>
  );
}